import { Dispatch, SetStateAction, useCallback } from 'react';
import Text from 'common/ui/Text';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Close from '@mui/icons-material/Close';
import { Stack, Spacer } from 'common/ui/Layout';
import {
  SettingsCard,
  SettingsCardContent,
  SettingsCardHeader,
  SettingsSeparator,
} from './SettingsLayoutComponents';
import { telemetryContextFor } from 'modules/Apollo/types';
import TextCss, { TextSx } from 'styles/text';
import SettingsDropdown from 'common/SettingsDropdown';
import { GET_ME, UPDATE_GENERAL_SETTINGS } from 'modules/Apollo/queries';
import { useMutation } from '@apollo/client';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { DialogSettingsSection } from './DialogSettingsSection';

const TOAST_DURATION_OPTIONS = [
  { value: 2000, name: 'Short (2s)' },
  { value: 4000, name: 'Medium (4s)' },
  { value: 6000, name: 'Long (6s)' },
];

type GeneralSettingsTabProps = {
  onClose: () => void;
  setError: Dispatch<SetStateAction<string | null>>;
};

export const GeneralSettingsTab = ({ onClose }: GeneralSettingsTabProps): React.ReactElement => {
  const [updateGeneralSettings] = useMutation(UPDATE_GENERAL_SETTINGS, {
    context: APOLLO_QUERY_CONTEXT,
  });

  const { data } = useCurrentUser({ context: APOLLO_QUERY_CONTEXT });
  const generalSettings = data?.me.generalSettings;

  const updateNotificationSettings = useCallback(
    async (input: { duration?: number; visibilityMode?: string }) => {
      const updatedResponse = {
        ...generalSettings,
        notifications: {
          ...generalSettings?.notifications,
          ...input,
        },
      } as const;

      await updateGeneralSettings({
        variables: {
          ...generalSettings,
          notifications: {
            ...generalSettings?.notifications,
            ...input,
          },
        },
        optimisticResponse: {
          __typename: 'Mutation',
          updateGeneralSettings: {
            __typename: 'GeneralSettings',
            ...updatedResponse,
          },
        },
        update: (proxy) => {
          proxy.writeQuery({
            query: GET_ME,
            data: {
              me: {
                ...data?.me,
                generalSettings: {
                  ...data?.me?.generalSettings,
                  notifications: {
                    ...data?.me?.generalSettings?.notifications,
                    ...input,
                  },
                },
              },
            },
          });
        },
      });
    },
    [data, generalSettings, updateGeneralSettings]
  );
  return (
    <SettingsCard>
      <SettingsCardHeader>
        <Stack alignY="center">
          <Text variant="display2">General Settings</Text>
          <Spacer />
          <IconButton onClick={onClose} size="large">
            <Close />
          </IconButton>
        </Stack>
      </SettingsCardHeader>
      <SettingsCardContent>
        <Stack vertical space="small">
          <Text variant="display1" display="block">
            Notifications
          </Text>
          <Stack alignY="center" alignX="between">
            <Text>Visibility</Text>
            <RadioGroup
              row
              aria-label="visibility"
              value={generalSettings?.notifications?.visibilityMode}
              onChange={(evt) => {
                updateNotificationSettings({ visibilityMode: evt.target.value });
              }}
            >
              <FormControlLabel
                value="all"
                name="notifications-visibility"
                checked={generalSettings?.notifications?.visibilityMode === 'all'}
                control={<Radio color="primary" />}
                label={<span css="font-size: 12px;">All</span>}
                labelPlacement="end"
                sx={TextSx.small}
                data-testid="notifications-visibility-all"
              />
              <FormControlLabel
                value="errorsOnly"
                name="notifications-visibility"
                checked={generalSettings?.notifications?.visibilityMode === 'errorsOnly'}
                control={<Radio color="primary" />}
                label={<span css={TextCss.caption}>Errors only</span>}
                labelPlacement="end"
                data-testid="notifications-visibility-errorsOnly"
              />
            </RadioGroup>
          </Stack>
          <Stack alignY="center" alignX="between">
            <Text>Duration</Text>
            <SettingsDropdown
              data-testid="notifications-duration-dropdown"
              value={generalSettings?.notifications?.duration}
              onSelectedChange={(value) => {
                updateNotificationSettings({
                  duration: value as number,
                });
              }}
            >
              {TOAST_DURATION_OPTIONS.map((option) => (
                <MenuItem
                  id={`select-menuitem-${option.value}`}
                  value={option.value}
                  key={option.value}
                  data-testid={`${option.value}-menu-item`}
                >
                  {option.name}
                </MenuItem>
              ))}
            </SettingsDropdown>
          </Stack>
          <SettingsSeparator />
          <DialogSettingsSection />
        </Stack>
      </SettingsCardContent>
    </SettingsCard>
  );
};

GeneralSettingsTab.displayName = 'GeneralSettingsTab';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(GeneralSettingsTab);
